import React, { Fragment, useEffect, useState } from "react";
import { InfoCircleTwoTone } from "@ant-design/icons";
import { Table } from "antd";
import { useNavigate } from "react-router-dom";
import {
	formatKycCreatedOnDate,
	getGroupCompanyId,
	getGroupCompanyName,
	getGroupCompanyRowLabel,
} from "./kycHelpers";

const getSortOrder = (tableSorter, columnKey) => {
	if (tableSorter.field !== columnKey) {
		return null;
	}

	return tableSorter.order === "ASC" ? "ascend" : tableSorter.order === "DESC" ? "descend" : null;
};

const getCellValue = (record, keys) => {
	for (const key of keys) {
		const value = record?.[key];
		if (value !== undefined && value !== null && value !== "") {
			return value;
		}
	}

	return "-";
};

const formatRecordCount = (total, range) => {
	if (!total) {
		return "Showing 0 records";
	}

	return `Showing ${range[0]}-${range[1]} of ${total.toLocaleString()} records`;
};

const getCrmGuid = (record = {}) =>
	record.crmGuid ||
	record.CrmGuid ||
	record.crmGUID ||
	record.companyGuid ||
	record.CompanyGuid ||
	record.companyGUID ||
	"";

const KycAccountTab = ({
	tabTableData,
	isLoading,
	tableSorter,
	OnhandleTableSortColumn,
	onOpenMaterialsByCustomer,
	navigationContext = {},
}) => {
	const [totalRecords, setTotalRecords] = useState(0);
	const [pageNo, setPageNo] = useState(1);
	const navigate = useNavigate();

	useEffect(() => {
		setTotalRecords(Number(tabTableData.totalRecords || 0));
		setPageNo(Number(tabTableData.currentPage || 1));
	}, [tabTableData]);

	const handleTableAscDes = (pagination, filters, sorter) => {
		const sorterList = Array.isArray(sorter) ? sorter : sorter ? [sorter] : [];
		const activeSorter =
			sorterList.find((s) => s && s.order) ||
			sorterList.find((s) => {
				const f = s?.field;
				const fieldStr = Array.isArray(f) ? f[0] : f;
				return fieldStr === tableSorter.field || s?.columnKey === tableSorter.field;
			}) ||
			sorterList[0];
		const rawField = activeSorter?.field;
		const sortField =
			rawField !== undefined && rawField !== null && rawField !== ""
				? Array.isArray(rawField)
					? rawField[0]
					: rawField
				: activeSorter?.columnKey ?? tableSorter.field;

		let nextOrder = tableSorter.order;
		if (activeSorter?.order === "ascend") {
			nextOrder = "ASC";
		} else if (activeSorter?.order === "descend") {
			nextOrder = "DESC";
		} else {
			const pageChanged =
				pagination.current !== tabTableData.currentPage ||
				(pagination.pageSize != null &&
					tabTableData.pageSize != null &&
					pagination.pageSize !== tabTableData.pageSize);
			if (!pageChanged && String(sortField) === String(tableSorter.field)) {
				const o = String(tableSorter.order || "").toUpperCase();
				nextOrder = o === "DESC" ? "ASC" : "DESC";
			}
		}

		OnhandleTableSortColumn({
			sortColumn: sortField,
			orderBy: nextOrder,
			currentPage: pagination.current,
			pageSize: pagination.pageSize,
		});
	};

	const handlePrimaryContactClick = (event, record) => {
		event.stopPropagation();
		const contactSapCode = record.primaryContact_SAPCode || record.primaryContactSAPCode || record.sapCode;
		if (!contactSapCode) {
			return;
		}

		const groupCompanyId = getGroupCompanyId(record);
		navigate(`/kyc/primary-contact/${encodeURIComponent(contactSapCode)}`, {
			state: {
				returnTab: "account",
				fromAccountTab: true,
				groupCompanyId: groupCompanyId ? String(groupCompanyId) : "",
				groupCompanyName:
					navigationContext.groupCompanyName || getGroupCompanyRowLabel(record) || getGroupCompanyName(record),
				accountName: record.accountName,
				primaryContactName: record.primaryContact || record.primaryContactName,
			},
		});
	};

	const handlePartnerFunctionClick = (event, record) => {
		event.stopPropagation();
		const crmGuid = getCrmGuid(record);
		if (!crmGuid) {
			return;
		}

		const recordGroupId = getGroupCompanyId(record);
		const accountName = getCellValue(record, ["accountName", "companyName", "ispl_AccountName"]);
		const currency = getCellValue(record, [
			"transactionCurrencyId",
			"currency",
			"ispl_Currency",
		]);
		const groupCompanyId =
			recordGroupId || navigationContext.groupCompanyId || "";
		const groupCompanyName =
			navigationContext.groupCompanyName ||
			getGroupCompanyRowLabel(record) ||
			getGroupCompanyName(record) ||
			"";
		const returnTab =
			navigationContext.activeCompanyTab === "materials" ? "materials" : "account";

		navigate(`/kyc/partner-function/${encodeURIComponent(crmGuid)}`, {
			state: {
				groupCompanyId: groupCompanyId ? String(groupCompanyId) : "",
				groupCompanyName: groupCompanyName !== "-" ? String(groupCompanyName).trim() : "",
				accountName: accountName !== "-" ? String(accountName).trim() : "",
				currency: currency !== "-" ? String(currency).trim() : "",
				returnTab,
				materialsCustomer: navigationContext.materialsIsplCustomer || "",
			},
		});
	};

	const handleMaterialsInfoClick = (event, record) => {
		event.stopPropagation();
		onOpenMaterialsByCustomer?.(record);
	};

	const tableColumns = [
		{
			title: "Group Company",
			key: "groupCompany",
			sorter: true,
			field: "groupCompany",
			showSorterTooltip: false,
			width: 220,
			sortOrder: getSortOrder(tableSorter, "groupCompany"),
			render: (_, record) => <span>{getCellValue(record, ["groupCompany"])}</span>,
		},
		{
			title: "Company Name",
			key: "accountName",
			sorter: true,
			field: "accountName",
			showSorterTooltip: false,
			width: 180,
			sortOrder: getSortOrder(tableSorter, "accountName"),
			render: (_, record) => <span>{getCellValue(record, ["accountName"])}</span>,
		},
		{
			title: "Relation Type",
			key: "relation_Type",
			sorter: true,
			field: "relation_Type",
			showSorterTooltip: false,
			width: 160,
			sortOrder: getSortOrder(tableSorter, "relation_Type"),
			render: (_, record) => <span>{getCellValue(record, ["relation_Type"])}</span>,
		},
		{
			title: "SAP Code",
			key: "sapCode",
			sorter: true,
			field: "sapCode",
			showSorterTooltip: false,
			width: 170,
			sortOrder: getSortOrder(tableSorter, "sapCode"),
			render: (_, record) => <span>{getCellValue(record, ["sapCode", "ispl_SAPCode"])}</span>,
		},
		{
			title: "Account Code",
			key: "ispl_AccountCode",
			sorter: true,
			field: "ispl_AccountCode",
			showSorterTooltip: false,
			width: 180,
			sortOrder: getSortOrder(tableSorter, "ispl_AccountCode"),
			render: (_, record) => <span>{getCellValue(record, ["ispl_AccountCode"])}</span>,
		},
		{
			title: "Currency",
			key: "transactionCurrencyId",
			sorter: true,
			field: "transactionCurrencyId",
			showSorterTooltip: false,
			width: 180,
			sortOrder: getSortOrder(tableSorter, "transactionCurrencyId"),
			render: (_, record) => <span>{getCellValue(record, ["transactionCurrencyId"])}</span>,
		},
		{
			title: "KPPL Division New",
			key: "ispl_KPPLDivisionNew",
			sorter: true,
			field: "ispl_KPPLDivisionNew",
			showSorterTooltip: false,
			width: 220,
			sortOrder: getSortOrder(tableSorter, "ispl_KPPLDivisionNew"),
			render: (_, record) => <span>{getCellValue(record, ["ispl_KPPLDivisionNew", "KPPLDivisionNew"])}</span>,
		},
		{
			title: "Sales District",
			key: "ispl_SalesDistrict",
			sorter: true,
			field: "ispl_SalesDistrict",
			showSorterTooltip: false,
			width: 180,
			sortOrder: getSortOrder(tableSorter, "ispl_SalesDistrict"),
			render: (_, record) => <span>{getCellValue(record, ["ispl_SalesDistrict", "salesDistrict"])}</span>,
		},
		{
			title: "Sales Office",
			key: "ispl_SalesOffice",
			sorter: true,
			field: "ispl_SalesOffice",
			showSorterTooltip: false,
			width: 180,
			sortOrder: getSortOrder(tableSorter, "ispl_SalesOffice"),
			render: (_, record) => <span>{getCellValue(record, ["ispl_SalesOffice", "salesOffice"])}</span>,
		},
		{
			title: "Sales Type (Price Group)",
			dataIndex: "ispl_PriceGroupSalestype",
			key: "ispl_PriceGroupSalestype",
			sorter: true,
			showSorterTooltip: false,
			width: 140,
			sortOrder: getSortOrder(tableSorter, "ispl_PriceGroupSalestype"),
			render: (_, record) => <span>{getCellValue(record, ["ispl_PriceGroupSalestype", "PriceGroupSalestype"])}</span>,
		},
		{
			title: "Distribution Channel",
			dataIndex: "ispl_DistributionChannel",
			key: "ispl_DistributionChannel",
			sorter: true,
			showSorterTooltip: false,
			width: 160,
			sortOrder: getSortOrder(tableSorter, "ispl_DistributionChannel"),
			render: (_, record) => <span>{getCellValue(record, ["ispl_DistributionChannel", "distributionChannel"])}</span>,
		},
		{
			title: "Primary Contact",
			dataIndex: "primaryContactName",
			key: "primaryContactName",
			sorter: true,
			showSorterTooltip: false,
			width: 180,
			sortOrder: getSortOrder(tableSorter, "primaryContactName"),
			render: (_, record) => {
				const label = record.primaryContact || record.primaryContactName || "-";
				const contactSapCode = record.primaryContact_SAPCode || record.primaryContactSAPCode || record.sapCode;

				if (!contactSapCode || label === "-") {
					return <span>{label}</span>;
				}

				return (
					<span className="kyc-primary-contact-link" onClick={(event) => handlePrimaryContactClick(event, record)}>
						{label}
					</span>
				);
			},
		},
		{
			title: "Address 1",
			dataIndex: "address1_Line1",
			key: "address1_Line1",
			sorter: true,
			showSorterTooltip: false,
			width: 180,
			sortOrder: getSortOrder(tableSorter, "address1_Line1"),
			render: (_, record) => <span>{getCellValue(record, ["address1_Line1", "address1Line1"]) || "-"}</span>,
		},
		{
			title: "Address 2",
			dataIndex: "address1_Line2",
			key: "address1_Line2",
			sorter: true,
			showSorterTooltip: false,
			width: 180,
			sortOrder: getSortOrder(tableSorter, "address1_Line2"),
			render: (_, record) => <span>{getCellValue(record, ["address1_Line2", "address1Line2"]) || "-"}</span>,
		},
		{
			title: "Created On",
			dataIndex: "createdOn",
			key: "createdOn",
			sorter: true,
			showSorterTooltip: false,
			width: 180,
			sortOrder: getSortOrder(tableSorter, "createdOn"),
			render: (_, record) => {
				const createdOnDate = getCellValue(record, ["createdOn"]);
				return <span>{formatKycCreatedOnDate(createdOnDate)}</span>;
			},
		},
		{
			title: "Owner",
			dataIndex: "ownerid",
			key: "ownerid",
			sorter: true,
			showSorterTooltip: false,
			width: 180,
			sortOrder: getSortOrder(tableSorter, "ownerid"),
			render: (_, record) => <span>{getCellValue(record, ["ownerid"]) || "-"}</span>,
		},
		{
			title: "Action",
			key: "action",
			width: 120,
			fixed: "right",
			render: (_, record) => (
				<div className="kyc-action-icons">
					<div
						className={`tbl_info_details ${onOpenMaterialsByCustomer ? "" : "disabled"}`}
						onClick={(event) => handleMaterialsInfoClick(event, record)}
						role="presentation"
					>
						<InfoCircleTwoTone />
					</div>
					<div
						className={`tbl_info_details kyc-partner-function-icon ${getCrmGuid(record) ? "" : "disabled"}`}
						onClick={(event) => handlePartnerFunctionClick(event, record)}>
						<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
							<path
								d="M12.6 2.59961H15.6C15.8652 2.59961 16.1195 2.70497 16.3071 2.8925C16.4946 3.08004 16.6 3.33439 16.6 3.59961V14.5996C16.6 15.13 16.3893 15.6388 16.0142 16.0138C15.6391 16.3889 13.7162 16.5996 13.1858 16.5996C12.6553 16.5996 13.5608 16.3889 13.1858 16.0138C12.8107 15.6388 12.6 15.13 12.6 14.5996V1.59961C12.6 1.33439 12.4946 1.08004 12.3071 0.892503C12.1195 0.704966 11.8652 0.599609 11.6 0.599609H1.59998C1.33476 0.599609 1.08041 0.704966 0.892869 0.892503C0.705333 1.08004 0.599976 1.33439 0.599976 1.59961V13.5996C0.599976 14.3953 0.916046 15.1583 1.47866 15.7209C2.04126 16.2835 2.80433 16.5996 3.59998 16.5996H13.1858"
								fill="#E7F7FF"
							/>
							<path
								d="M12.6 2.59961H15.6C15.8652 2.59961 16.1195 2.70497 16.3071 2.8925C16.4946 3.08004 16.6 3.33439 16.6 3.59961V14.5996C16.6 15.13 16.3893 15.6388 16.0142 16.0138C15.6391 16.3889 13.7162 16.5996 13.1858 16.5996M13.1858 16.5996C12.6553 16.5996 13.5608 16.3889 13.1858 16.0138C12.8107 15.6388 12.6 15.13 12.6 14.5996V1.59961C12.6 1.33439 12.4946 1.08004 12.3071 0.892503C12.1195 0.704966 11.8652 0.599609 11.6 0.599609H1.59998C1.33476 0.599609 1.08041 0.704966 0.892869 0.892503C0.705333 1.08004 0.599976 1.33439 0.599976 1.59961V13.5996C0.599976 14.3953 0.916046 15.1583 1.47866 15.7209C2.04126 16.2835 2.80433 16.5996 3.59998 16.5996H13.1858ZM4.59998 4.59961H8.59998M4.59998 8.59961H8.59998M4.59998 12.5996H8.59998"
								stroke="#3BAFDA"
								strokeWidth="1.2"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					</div>
				</div>
			),
		},
	];

	return (
		<Fragment>
			<Table
				bordered
				scroll={{ x: 1600 }}
				className="logistic_tables kyc_logistic_tables"
				dataSource={tabTableData.rows}
				columns={tableColumns}
				sortDirections={["ascend", "descend"]}
				onChange={handleTableAscDes}
				rowKey={(record, index) =>
					record.id ||
					record.companyId ||
					record.accountId ||
					record.crmGuid ||
					record.ispl_AccountCode ||
					index
				}
				pagination={{
					total: totalRecords,
					pageNo: pageNo,
					pageSize: tabTableData.pageSize || 10,
					responsive: true,
					showSizeChanger: false,
					showTotal: formatRecordCount,
					current: pageNo,
					onChange: (pageNum) => setPageNo(pageNum),
				}}
				loading={isLoading}
			/>
		</Fragment>
	);
};

export default KycAccountTab;
